import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack
} from '@mui/material';
import { MatchData, MatchH2H } from '@/interfaces/liveGamesType';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { FiTrendingUp, FiCalendar } from 'react-icons/fi';
import { fetchH2H } from '@/hooks/fetchHooks/h2hFetch';
import { format } from 'date-fns';
import ptBr from 'date-fns/locale/pt-BR';
import { fakeH2HData } from '../MatchDetails/fakeData';

interface HistoricalTrendsProps {
  match: MatchData;
  isLoading?: boolean;
}

const HistoricalTrends: React.FC<HistoricalTrendsProps> = ({ match, isLoading: propIsLoading = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const {isAuthenticated, decodedToken} = useAuth()

  const { data: h2hData, isLoading: h2hLoading } = isAuthenticated && decodedToken.isActive
    ? fetchH2H(match.match_hometeam_id, match.match_awayteam_id)
    : {
        data: fakeH2HData,
        isLoading: false,
      };

  // Verificar se está carregando
  const isLoading = propIsLoading || h2hLoading;

  // Calcular estatísticas com base nos dados de h2h
  const stats = useMemo(() => {
    if (!h2hData?.firstTeam_VS_secondTeam?.length) {
      return {
        homeTeamWins: 0,
        awayTeamWins: 0,
        draws: 0,
        totalGoals: 0,
        averageGoals: 0,
        bothTeamsScored: 0,
        bothTeamsScoredPercentage: 0
      };
    }

    const matches = h2hData.firstTeam_VS_secondTeam;
    let homeTeamWins = 0;
    let awayTeamWins = 0;
    let draws = 0;
    let totalGoals = 0;
    let bothTeamsScored = 0;

    matches.forEach(h2hMatch => {
      const homeScore = parseInt(h2hMatch.match_hometeam_score) || 0;
      const awayScore = parseInt(h2hMatch.match_awayteam_score) || 0;
      
      // Contar vitórias e empates
      if (h2hMatch.match_hometeam_id === match.match_hometeam_id) {
        // Time da casa é o primeiro time (time A)
        if (homeScore > awayScore) {
          homeTeamWins++;
        } else if (homeScore < awayScore) {
          awayTeamWins++;
        } else {
          draws++;
        }
      } else if (h2hMatch.match_awayteam_id === match.match_hometeam_id) {
        // Time da casa é o segundo time (time B)
        if (homeScore < awayScore) {
          homeTeamWins++;
        } else if (homeScore > awayScore) {
          awayTeamWins++;
        } else {
          draws++;
        }
      }
      
      // Calcular total de gols
      totalGoals += homeScore + awayScore;
      
      // Verificar se ambos os times marcaram
      if (homeScore > 0 && awayScore > 0) {
        bothTeamsScored++;
      }
    });

    const matchesCount = matches.length;
    const averageGoals = matchesCount > 0 ? (totalGoals / matchesCount).toFixed(1) : 0;
    const bothTeamsScoredPercentage = matchesCount > 0 ? Math.round((bothTeamsScored / matchesCount) * 100) : 0;

    return {
      homeTeamWins,
      awayTeamWins,
      draws,
      totalGoals,
      averageGoals,
      bothTeamsScored,
      bothTeamsScoredPercentage
    };
  }, [h2hData, match.match_hometeam_id]);

  // Dados para o gráfico de tendências baseados nos últimos jogos
  const trendData = useMemo(() => {
    if (!h2hData?.firstTeam_VS_secondTeam?.length) {
      return [
        { name: '6 jogos atrás', home: 0, away: 0, total: 0 },
        { name: '5 jogos atrás', home: 0, away: 0, total: 0 },
        { name: '4 jogos atrás', home: 0, away: 0, total: 0 },
        { name: '3 jogos atrás', home: 0, away: 0, total: 0 },
        { name: '2 jogos atrás', home: 0, away: 0, total: 0 },
        { name: 'Último jogo', home: 0, away: 0, total: 0 },
      ];
    }

    // Ordenar partidas por data, da mais recente para a mais antiga
    const sortedMatches = [...h2hData.firstTeam_VS_secondTeam]
      .sort((a, b) => new Date(b.match_date).getTime() - new Date(a.match_date).getTime())
      .slice(0, 6); // Pegar até 6 jogos mais recentes

    // Preencher o array com os dados disponíveis
    return sortedMatches.map((match, index) => {
      const homeScore = parseInt(match.match_hometeam_score) || 0;
      const awayScore = parseInt(match.match_awayteam_score) || 0;
      const total = homeScore + awayScore;
      
      // Ajustar o nome com base no índice (para os jogos mais recentes primeiro)
      const gameNumber = sortedMatches.length - index;
      let name = `${gameNumber} jogos atrás`;
      if (index === sortedMatches.length - 1) {
        name = 'Último jogo';
      }

      return {
        name,
        home: homeScore,
        away: awayScore,
        total
      };
    }).reverse(); // Inverter para manter a ordem cronológica no gráfico
  }, [h2hData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FiTrendingUp style={{ marginRight: 8 }} /> Tendências Históricas
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Box sx={{ height: 250, mt: 2 }}>
            <Typography variant="body2" gutterBottom align="center">
              Gols por Partida (Últimos {trendData.length} Jogos)
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trendData}
                margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#333' : '#fff',
                    border: `1px solid ${theme.palette.divider}`
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="home"
                  name={match.match_hometeam_name}
                  stroke={theme.palette.homeColor.main}
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="away"
                  name={match.match_awayteam_name}
                  stroke={theme.palette.awayColor.main}
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="Total de Gols"
                  stroke={theme.palette.success.main}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={12}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom align="center" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiCalendar style={{ marginRight: 8 }} /> Confrontos Diretos
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell align="center" >Placar <br/> (Casa - Fora)</TableCell>
                    <TableCell>Competição</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {h2hData?.firstTeam_VS_secondTeam?.map((h2h, index) => (
                    <TableRow key={`h2h-${index}`}>
                      <TableCell>{format(new Date(h2h.match_date), 'dd/MM/yyyy', { locale: ptBr })}</TableCell>
                      <TableCell align="center" sx={{ padding: '8px', width: '140px' }}>
                        <Stack 
                          direction="row" 
                          spacing={1} 
                          alignItems="center" 
                          justifyContent="center"
                        >
                          <Box
                            component="img"
                            src={h2h.team_home_badge}
                            alt={h2h.match_hometeam_name}
                            width={24}
                            height={24}
                            onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${h2h.match_hometeam_name}` }}
                          />
                          <Box display="inline" fontWeight="bold" fontSize="0.875rem">
                            {h2h.match_hometeam_score} - {h2h.match_awayteam_score}
                          </Box>
                          <Box
                            component="img"
                            src={h2h.team_away_badge}
                            alt={h2h.match_awayteam_name}
                            width={24}
                            height={24}
                            ml={1}
                            onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${h2h.match_awayteam_name}` }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>{h2h.league_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Vitórias {match.match_hometeam_name}</Typography>
              <Typography variant="h6" color="homeColor.main">{stats.homeTeamWins}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Empates</Typography>
              <Typography variant="h6">{stats.draws}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Vitórias {match.match_awayteam_name}</Typography>
              <Typography variant="h6" color="awayColor.main">{stats.awayTeamWins}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Média de Gols</Typography>
              <Typography variant="h6" color="success.main">{stats.averageGoals}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Ambas Marcaram</Typography>
              <Typography variant="h6">{stats.bothTeamsScoredPercentage}%</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default HistoricalTrends;